import type { AnyActorRef } from 'xstate';
import { assign, fromCallback, fromPromise, sendTo, setup } from 'xstate';
import type { ServiceError } from '~/features/alerts/errors';
import { createErrorAssigner, handleServiceError } from '~/features/alerts/errors';
import type { ProviderCategory } from '~/types';
import * as api from './api';
import { ProviderType_, TypeAndCategoryValues, WelcomeValues } from './types';

/**
 * Provider Registration Service Machine
 *
 * PURE BUSINESS LOGIC & DATA MACHINE
 *
 * Responsibilities:
 * - API calls (fetch categories/types, update provider, upload CV, create role)
 * - Business rule validation
 * - Data persistence
 * - Registration completion flow
 *
 * States are business logic states, NOT UI states:
 * - idle: No active operation
 * - fetchingCategories: Loading categories
 * - fetchingTypes: Loading types for a category
 * - updatingProvider: Saving provider info
 * - uploadingCV: Uploading CV file
 * - creatingUserRole: Creating user role
 * - completed: Registration finished successfully
 *
 * Note: UI state (routes, page navigation) is handled by the VIEW machine.
 * This machine only cares about business data and operations.
 */

/**
 * Parse-watch budget. 4s x 45 = 3 minutes, comfortably past a typical CV parse
 * while keeping the request count small (the wizard has exactly one document).
 */
export const PARSE_POLL_INTERVAL_MS = 4000;
export const PARSE_POLL_MAX_ATTEMPTS = 45;

// Custom error messages for registration service
const registrationErrorMap = {
  400: { code: 'VALIDATION_ERROR', message: 'Invalid registration data' },
  401: { code: 'UNAUTHORIZED', message: 'Please log in to complete registration' },
  403: { code: 'FORBIDDEN', message: 'You do not have permission to register' },
  404: { code: 'NOT_FOUND', message: 'Registration resource not found' },
  409: { code: 'CONFLICT', message: 'Provider already exists' },
  413: { code: 'FILE_TOO_LARGE', message: 'CV file is too large' },
  415: { code: 'INVALID_FILE_TYPE', message: 'Invalid CV file type' },
  500: { code: 'SERVER_ERROR', message: 'Server error occurred during registration' },
};

export const providerRegistrationService = setup({
  types: {
    context: {} as {
      // Business data
      providerId: number | null;
      categories: ProviderCategory[];
      userInfoId: number;
      types: ProviderType_[];
      loading: boolean;
      error: ServiceError | null;
      registered: boolean;
      /** Document whose AI parse $_AWAITING_PARSE is currently watching. */
      parsingDocumentId: number | null;
      // Reference to parent actor for sending events
      parentRef: AnyActorRef;
    },
    events: {} as
      | {
          type: 'CREATE_PROVIDER';
          data: { typeAndCategory: TypeAndCategoryValues; welcome: WelcomeValues };
        }
      // A single `File`, not the source `FileList`: the view stages the picked
      // file while it asks the doc type question, and `dataTransfer.files` is
      // only readable inside the drop handler. [MCV-1593]
      | { type: 'UPLOAD_CV'; file: File; docType: 'cv' | 'packet' }
      | { type: 'CREATE_USER_ROLE' }
      | { type: 'SET_WELCOME_FORM'; data: WelcomeValues }
      | { type: 'SET_ERROR'; error: ServiceError }
      // Emitted by the watchParse callback actor
      | { type: 'PARSE_COMPLETE' }
      | { type: 'PARSE_FAILED' }
      | { type: 'PARSE_UNKNOWN' },
    input: {} as {
      userInfoId: number;
      parentRef: AnyActorRef;
    },
  },
  actors: {
    fetchCategories: fromPromise(async () => {
      return await api.fetchCategories();
    }),
    fetchTypes: fromPromise(async () => {
      return await api.fetchTypes();
    }),
    createProvider: fromPromise(
      async ({
        input,
      }: {
        input: {
          userInfoId: number;
          typeAndCategory: TypeAndCategoryValues;
          welcome: WelcomeValues;
        };
      }) => {
        return await api.createProvider(input);
      }
    ),
    uploadCV: fromPromise(
      async ({
        input,
      }: {
        input: { providerId: number | null; file: File; docType: 'cv' | 'packet' };
      }) => {
        return await api.uploadCV(input.providerId, input.file, input.docType);
      }
    ),
    createUserRole: fromPromise(async ({ input }: { input: { userInfoId: number } }) => {
      return await api.createUserRole(input.userInfoId);
    }),
    /**
     * Watch an uploaded document until its AI parse settles.
     *
     * Bounded on purpose: a parse that outlives the budget leaves the wizard
     * saying "still working in the background" rather than spinning forever.
     */
    watchParse: fromCallback<
      { type: string },
      { providerId: number | null; documentId: number | undefined }
    >(({ input, sendBack }) => {
      const { providerId, documentId } = input;
      if (!providerId || !documentId) {
        sendBack({ type: 'PARSE_UNKNOWN' });
        return;
      }

      let timer: ReturnType<typeof setTimeout> | undefined;
      let stopped = false;
      let attempts = 0;

      const stop = () => {
        stopped = true;
        if (timer) clearTimeout(timer);
      };

      const poll = async () => {
        if (stopped) return;
        attempts += 1;
        try {
          const status = await api.fetchParseStatus(providerId, documentId);
          if (stopped) return;
          if (status === 'complete') return sendBack({ type: 'PARSE_COMPLETE' });
          if (status === 'failed') return sendBack({ type: 'PARSE_FAILED' });
        } catch (error) {
          // A transient poll failure is not a parse failure -- keep watching
          // and let the attempt budget decide when to give up.
          console.debug('[SERVICE] parse status poll failed:', error);
        }
        if (stopped) return;
        if (attempts >= PARSE_POLL_MAX_ATTEMPTS) return sendBack({ type: 'PARSE_UNKNOWN' });
        timer = setTimeout(poll, PARSE_POLL_INTERVAL_MS);
      };

      timer = setTimeout(poll, PARSE_POLL_INTERVAL_MS);
      return stop;
    }),
  },
  actions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    handleServiceError: handleServiceError as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    assignRegistrationError: createErrorAssigner(registrationErrorMap) as any,
  },
}).createMachine({
  id: 'providerRegistrationService',
  initial: '$_FETCHING_CATEGORIES',
  context: ({ input }) => ({
    loading: false,
    userInfoId: input.userInfoId,
    registered: false,
    providerId: null,
    parsingDocumentId: null,
    categories: [],
    types: [],
    error: null,
    parentRef: input.parentRef,
  }),
  output: ({ context }) => {
    if (context.error) {
      return {
        status: 'error' as const,
        error: context.error,
        code: context.error.code || 'REGISTRATION_ERROR',
      };
    }
    return {
      status: 'success' as const,
      providerId: context.providerId ?? 0,
      registered: context.registered,
      userInfoId: context.userInfoId,
      roleCode: 'PROVIDER',
    };
  },
  states: {
    $_IDLE: {
      entry: [assign({ error: null })],
      on: {
        CREATE_PROVIDER: {
          target: '$_CREATING_PROVIDER',
        },
        UPLOAD_CV: {
          target: '$_UPLOADING_CV',
        },
        CREATE_USER_ROLE: {
          target: '$_CREATING_USER_ROLE',
        },
      },
    },
    $_FETCHING_CATEGORIES: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'fetchCategories',
        onDone: {
          target: '$_FETCHING_TYPES',
          actions: [
            assign({
              categories: ({ event }) => event.output,
              loading: () => false,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
          ],
        },
      },
    },
    $_FETCHING_TYPES: {
      entry: [
        assign({
          loading: true,
          error: null,
          types: [], // Clear previous types when fetching new ones
        }),
      ],
      invoke: {
        src: 'fetchTypes',
        onDone: {
          target: '$_IDLE',
          actions: [
            assign({
              types: ({ event }) => event.output,
              loading: () => false,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
          ],
        },
      },
    },
    $_CREATING_PROVIDER: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'createProvider',
        input: ({ event, context }) => {
          const data = (
            event as {
              type: string;
              data: {
                typeAndCategory: TypeAndCategoryValues;
                welcome: WelcomeValues;
              };
            }
          ).data;
          return {
            userInfoId: context.userInfoId,
            typeAndCategory: data.typeAndCategory,
            welcome: data.welcome,
          };
        },
        onDone: {
          target: '$_IDLE',
          actions: [
            assign({
              providerId: ({ event }) => event.output.id ?? null,
              loading: () => false,
            }),
            // Send event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_PROVIDER_CREATED',
                providerId: event.output.id,
              })
            ),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => {
              console.error(
                '[SERVICE] $_CREATING_PROVIDER: createProvider onError, error:',
                event.error
              );
            },
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
            // Send error event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_SERVICE_ERROR',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState errors can be any type
                error: event.error as any,
              })
            ),
          ],
        },
      },
    },
    $_UPLOADING_CV: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'uploadCV',
        input: ({ context, event }) => {
          const typed = event as {
            type: string;
            file: File;
            docType: 'cv' | 'packet';
          };
          return {
            providerId: context.providerId,
            file: typed.file,
            docType: typed.docType,
          };
        },
        onDone: {
          // The upload is done but the AI parse has only just been queued, so
          // hand off to the watcher rather than going idle.
          target: '$_AWAITING_PARSE',
          actions: [
            assign({
              parsingDocumentId: ({ event }) =>
                (event.output as { id?: number } | undefined)?.id ?? null,
            }),
            // Send event to parent view machine. The uploadCV output carries the
            // stored document, and the view machine reads event.documentReference
            // off it -- a bare event would leave that permanently undefined.
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_CV_UPLOADED',
                documentReference: event.output as { id: number; docName: string } | undefined,
              })
            ),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => {
              console.error('[SERVICE] $_UPLOADING_CV: uploadCV onError, error:', event.error);
            },
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
            // Send error event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_SERVICE_ERROR',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState errors can be any type
                error: event.error as any,
              })
            ),
          ],
        },
      },
    },
    /**
     * Watches the queued AI parse. Every exit reports one settled outcome to the
     * view machine so the spinner can never be left running.
     */
    $_AWAITING_PARSE: {
      entry: [assign({ loading: () => false })],
      invoke: {
        src: 'watchParse',
        input: ({ context }) => ({
          providerId: context.providerId,
          documentId: context.parsingDocumentId ?? undefined,
        }),
      },
      on: {
        PARSE_COMPLETE: {
          target: '$_IDLE',
          actions: sendTo(({ context }) => context.parentRef, {
            type: 'ON_PARSE_SETTLED',
            outcome: 'complete',
          }),
        },
        PARSE_FAILED: {
          target: '$_IDLE',
          actions: sendTo(({ context }) => context.parentRef, {
            type: 'ON_PARSE_SETTLED',
            outcome: 'failed',
          }),
        },
        PARSE_UNKNOWN: {
          target: '$_IDLE',
          actions: sendTo(({ context }) => context.parentRef, {
            type: 'ON_PARSE_SETTLED',
            outcome: 'unknown',
          }),
        },
        // The user can finish registering while the parse is still running; the
        // invoked watcher is torn down automatically on state exit.
        CREATE_USER_ROLE: { target: '$_CREATING_USER_ROLE' },
        UPLOAD_CV: { target: '$_UPLOADING_CV' },
      },
    },
    $_CREATING_USER_ROLE: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'createUserRole',
        input: ({ context }) => {
          return { userInfoId: context.userInfoId };
        },
        onDone: {
          target: '$_COMPLETED',
          actions: [
            assign({
              registered: () => true,
              loading: () => false,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => {
              console.error(
                '[SERVICE] $_CREATING_USER_ROLE: createUserRole onError, error:',
                event.error
              );
            },
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
            // Send error event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_SERVICE_ERROR',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState errors can be any type
                error: event.error as any,
              })
            ),
          ],
        },
      },
    },
    $_COMPLETED: {
      entry: [
        // Notify parent that registration is complete
        sendTo(({ context }) => context.parentRef, {
          type: 'ON_REGISTRATION_COMPLETE',
        }),
      ],
      output: ({ context }) => {
        return {
          status: 'success' as const,
          providerId: context.providerId ?? 0,
          registered: true,
          userInfoId: context.userInfoId,
          roleCode: 'PROVIDER',
        };
      },
      type: 'final' as const,
    },
  },
});
